import { Injectable } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  LoginCredentialContent,
  LoginErrorMessages
} from '@com-delta-idp/mnx-login-ui';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { FlyingClubValidator } from '../../validator/flyingclub-validator';
import { PasswordValidor } from '../../validator/password-validator';
import { PatternValidator } from '../../validator/pattern-validator';
import { regexList } from '../../validator/regex-list';
import { UsernameValidator } from '../../validator/username-validator';
import { ErrorMessage, LoginModalContent } from '../models/aem';

@Injectable({
  providedIn: 'root'
})
export class LoginModalHelperService {
  loginCredentialsValidationForm!: UntypedFormGroup;
  isUserNameEntered(value: string): boolean {
    if (
      value.match(<RegExp>regexList.userIdLength) &&
      value.match(<RegExp>regexList.userName) &&
      value.match(<RegExp>regexList.isNaN)
    ) {
      return true;
    }
    return false;
  }

  isEmailEntered(value: string): boolean {
    if (
      value.match(<RegExp>regexList.userIdLength) &&
      value.match(<RegExp>regexList.email) &&
      value.match(<RegExp>regexList.isNaN)
    ) {
      return true;
    }
    return false;
  }

  createLoginForm(config: OauthAEMConfig): UntypedFormGroup {
    this.loginCredentialsValidationForm = new UntypedFormGroup({
      userId: new UntypedFormControl(null, [
        Validators.required,
        this.setUserIdFormControl(config)
      ]),
      lastName: new UntypedFormControl(null, [
        Validators.required,
        PatternValidator(regexList.lastName),
        PatternValidator(regexList.lastNameLength)
      ]),
      password: new UntypedFormControl(null, [
        Validators.required,
        PasswordValidor
      ]),
      keepMeLoggedIn: new UntypedFormControl({ value: false }, [])
    });
    return this.loginCredentialsValidationForm;
  }

  getLoginFormGroup(): UntypedFormGroup {
    return this.loginCredentialsValidationForm;
  }

  setUserIdFormControl(config: OauthAEMConfig) {
    return config.IS_EMAIL_ALLOWED ? FlyingClubValidator : UsernameValidator;
  }

  getFormControlName(control: string): string | void | undefined {
    for (const key of Object.keys(
      this.loginCredentialsValidationForm.controls
    )) {
      if (key === control) return key;
    }
  }

  getLoginErrorMessage(
    loginModalContent: LoginModalContent
  ): LoginErrorMessages {
    let userNameErrorMessages = '',
      passwordErrorMessages = '',
      lastNameErrorMessages = '',
      alertWarningErrorMessages = '';
    loginModalContent.errors.errorMessages.forEach((option: ErrorMessage) => {
      if (option['errorId'] === this.getFormControlName('userId')) {
        userNameErrorMessages = option['errorMessageDescription'];
      }
      if (option['errorId'] === this.getFormControlName('password')) {
        passwordErrorMessages = option['errorMessageDescription'];
      }
      if (option['errorId'] === this.getFormControlName('lastName')) {
        lastNameErrorMessages = option['errorMessageDescription'];
      }
      alertWarningErrorMessages = 'To continue, please correct the';
    });
    return {
      usernameErrorMessage: userNameErrorMessages,
      passwordErrorMessage: passwordErrorMessages,
      lastNameErrorMessage: lastNameErrorMessages,
      alertWarning: alertWarningErrorMessages
    };
  }

  getLoginAuthenticationScreenContent(
    loginModalContent: LoginModalContent,
    config: OauthAEMConfig
  ): LoginCredentialContent {
    const { loginCredentials } = loginModalContent;
    const { footer } = loginCredentials;
    footer.accountRecoveryFlowEnabled = config.ACCOUNT_RECOVERY_FLOW_ENABLED;
    const headerObj = {
      headerTitle: loginCredentials.headerTitle,
      headerSubTitle: loginCredentials.headerSubTitle,
      enterPasswordText: loginCredentials.enterPasswordText
    };
    return {
      pageHeader: Object.assign(loginModalContent?.pageHeader, headerObj),
      errors: this.getLoginErrorMessage(loginModalContent),
      ...loginCredentials
    };
  }

  getLoginFormAttributes() {
    return {
      errorCount: 0,
      submitAttempt: false,
      isLastNameRequired: false,
      errorAlertBanner: false,
      userIdControlTouched: false,
      passwordControlTouched: false,
      lastNameControlTouched: false,
      keepMeLoggedInTouched: false,
      formAlertSuccessIcon: false
    };
  }

  setFormGroup(formGroup: UntypedFormGroup) {
    this.loginCredentialsValidationForm = formGroup;
  }
}
